import type { AccessPasswordHandling, AccessQueueItemPublic } from '@accounts/shared';
import { ACCESS_PASSWORD_HANDLINGS } from '@accounts/shared';
import { useState } from 'react';

import { STR } from '../../../../i18n';
import { Modal, ModalForm } from '../../../../ui/Modal';
import { Callout } from '../../../../ui/Primitives';
import {
  fieldLabelStyle,
  hintStyle,
  modalButtonStyle,
  rowActionStyle,
  textFieldStyle,
} from '../../../../ui/styles';
import { itemLabel, passwordHandlingLabel } from '../accessFormat';

/**
 * APPROVE, WITH THE PASSWORD MODE FIRST (board, 18 Oct, round 3 item 5). An outside system
 * is not connected to Accounts, so how the person will use its password — Managed or Given
 * raw — is decided before the approval, because offboarding later reads it. The confirm
 * button stays disabled until every outside row has a pick; suite-app rows ride along with
 * no password step. Only the MODE is ever asked for: there is no password field anywhere.
 */
export function ApproveDialog({
  items,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  items: readonly AccessQueueItemPublic[];
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (modes: Readonly<Record<string, AccessPasswordHandling>>) => void;
}) {
  const [picks, setPicks] = useState<Record<string, AccessPasswordHandling>>({});
  const outside = items.filter((item) => item.catalogItem.applicationId === null);
  const suite = items.filter((item) => item.catalogItem.applicationId !== null);
  // A suite-app row has no password step, but its requester's note is still shown.
  const suiteWithNote = suite.filter((item) => item.note !== null);
  const missing = outside.filter((item) => picks[item.id] === undefined).length;
  const first = items[0];

  const title =
    items.length === 1 && first !== undefined
      ? STR.access.queue.approveTitleOne(itemLabel(first.path, first.catalogItem.name), first.user.name)
      : STR.access.queue.approveTitleMany(items.length);

  function setAll(mode: AccessPasswordHandling) {
    setPicks(Object.fromEntries(outside.map((item) => [item.id, mode] as const)));
  }

  return (
    <Modal
      title={title}
      lead={STR.access.queue.approveLead}
      width="min(640px,100%)"
      dismissable={!pending}
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            className="ghost"
            style={modalButtonStyle}
            disabled={pending}
            onClick={onCancel}
          >
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="prim"
            style={modalButtonStyle}
            disabled={pending || missing > 0}
            onClick={() => {
              onConfirm(picks);
            }}
          >
            {pending
              ? STR.access.queue.approving
              : missing > 0
                ? STR.access.queue.pickMore(missing)
                : items.length > 1
                  ? STR.access.queue.approveGoMany(items.length)
                  : STR.access.queue.approveGo}
          </button>
        </>
      }
    >
      <ModalForm>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12 }}>
          {ACCESS_PASSWORD_HANDLINGS.map((mode) => (
            <div
              key={mode}
              style={{ border: '1px solid var(--border)', borderRadius: 9, padding: '9px 11px' }}
            >
              <b>{passwordHandlingLabel(mode)}</b>
              <div className="tx-subtle">
                {mode === 'managed' ? STR.access.queue.managedCard : STR.access.queue.givenRawCard}
              </div>
            </div>
          ))}
        </div>

        {outside.length > 1 ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
            <span className="tx-subtle">{STR.access.queue.setAll(outside.length)}</span>
            {ACCESS_PASSWORD_HANDLINGS.map((mode) => (
              <button
                key={mode}
                type="button"
                className="ghost"
                style={rowActionStyle}
                disabled={pending}
                onClick={() => {
                  setAll(mode);
                }}
              >
                {passwordHandlingLabel(mode)}
              </button>
            ))}
          </div>
        ) : null}

        {outside.length > 0 ? (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              border: '1px solid var(--border)',
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            {outside.map((item) => (
              <li
                key={item.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0,1fr) auto',
                  gap: 12,
                  alignItems: 'center',
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: 12.5,
                }}
              >
                <span style={{ minWidth: 0 }}>
                  <b style={{ fontWeight: 650 }}>{item.user.name}</b>
                  {STR.accessCommon.pathSeparator}
                  {itemLabel(item.path, item.catalogItem.name)}
                  <NoteLine note={item.note} />
                </span>
                <span
                  className="tgl"
                  role="group"
                  aria-label={STR.access.queue.passwordFor(item.user.name)}
                >
                  {ACCESS_PASSWORD_HANDLINGS.map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className="seg"
                      aria-pressed={picks[item.id] === mode}
                      disabled={pending}
                      onClick={() => {
                        setPicks((current) => ({ ...current, [item.id]: mode }));
                      }}
                      style={{ padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 650 }}
                    >
                      {passwordHandlingLabel(mode)}
                    </button>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {suite.length > 0 ? (
          <div className="tx-subtle" style={{ fontSize: 12.5 }}>
            {STR.access.queue.suiteAlongside(
              suite.length,
              [...new Set(suite.map((item) => item.catalogItem.name))].join(', '),
            )}
          </div>
        ) : null}
        {suiteWithNote.length > 0 ? (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
            {suiteWithNote.map((item) => (
              <li key={item.id} style={{ minWidth: 0, fontSize: 12.5 }}>
                <b style={{ fontWeight: 650 }}>{item.user.name}</b>
                {STR.accessCommon.pathSeparator}
                {itemLabel(item.path, item.catalogItem.name)}
                <NoteLine note={item.note} />
              </li>
            ))}
          </ul>
        ) : null}
        {error === null ? null : (
          <Callout tone="danger" role="alert">
            {error}
          </Callout>
        )}
      </ModalForm>
    </Modal>
  );
}

/** The requester's note (K6), one line under the row; the full text is on hover. */
function NoteLine({ note }: { note: string | null }) {
  if (note === null) return null;
  return (
    <span
      className="tx-subtle"
      title={note}
      style={{
        display: 'block',
        fontSize: 11.5,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {STR.access.queue.noteQuoted(note)}
    </span>
  );
}

/** DENY — a reason is required (the server refuses a deny without one). */
export function DenyDialog({
  items,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  items: readonly AccessQueueItemPublic[];
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const trimmed = note.trim();
  const first = items[0];

  const title =
    items.length === 1 && first !== undefined
      ? STR.access.queue.denyTitleOne(itemLabel(first.path, first.catalogItem.name), first.user.name)
      : STR.access.queue.denyTitleMany(items.length);

  function submit() {
    setTried(true);
    if (trimmed === '') return;
    onConfirm(trimmed);
  }

  return (
    <Modal
      title={title}
      lead={STR.access.queue.denyLead}
      dismissable={!pending}
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            className="ghost"
            style={modalButtonStyle}
            disabled={pending}
            onClick={onCancel}
          >
            {STR.common.cancel}
          </button>
          <button
            type="button"
            className="dbtn"
            style={modalButtonStyle}
            disabled={pending}
            onClick={submit}
          >
            {pending
              ? STR.access.queue.denying
              : items.length > 1
                ? STR.access.queue.denyGoMany(items.length)
                : STR.access.queue.denyGo}
          </button>
        </>
      }
    >
      <ModalForm>
        <label>
          <span style={fieldLabelStyle}>{STR.access.queue.denyNoteLabel}</span>
          <textarea
            className="field"
            rows={3}
            value={note}
            placeholder={STR.access.queue.denyNotePlaceholder}
            aria-invalid={tried && trimmed === ''}
            onChange={(event) => {
              setNote(event.target.value);
            }}
            style={{ ...textFieldStyle, lineHeight: 1.5, resize: 'vertical' }}
          />
          {tried && trimmed === '' ? (
            <span className="tx-danger-strong" role="alert" style={hintStyle}>
              {STR.access.queue.denyNoteRequired}
            </span>
          ) : null}
        </label>
        {error === null ? null : (
          <Callout tone="danger" role="alert">
            {error}
          </Callout>
        )}
      </ModalForm>
    </Modal>
  );
}
